/**
 * Unified Property Interface based on real MongoDB data structure
 * This standardizes access to property data across all dealTypes (buy, rent, primary)
 * 
 * PUBLIC vs AUTHENTICATED ACCESS:
 * - PUBLIC: Basic property data without creatorFields, urls, realtorFields.ownerUserId
 * - AUTHENTICATED (realtors): Enhanced data with creatorFields, urls, and full realtorFields
 */

export interface StandardMainInfo {
  title: string;
  description?: string;
  objectType: string;
  class?: string;
  area?: number;
  rooms?: number;
  bedrooms?: number;
  bathrooms?: number;
  ceilingHeight?: number;
  address?: string;
  landArea?: number;
  _id?: string;
}

export interface StandardLocation {
  location?: {
    type: string;
    coordinates: [number, number];
  };
  country?: string;
  region?: string;
  city?: string;
  district?: string;
  microdistrict?: string;
  _id?: string;
}

export interface StandardPricing {
  pricePerSqM?: number;
  totalPrice?: number;
  currency?: string;
  oldPrice?: number;
  convertedPrices?: {
    USD?: number;
    EUR?: number;
    UAH?: number;
    GEL?: number;
  };
  convertedPricesPerSqM?: {
    USD?: number;
    EUR?: number;
    UAH?: number;
    GEL?: number;
  };
  // For PRIMARY deal type with ranges
  convertedPricesMin?: {
    USD?: number;
    EUR?: number;
    UAH?: number;
    GEL?: number;
  };
  convertedPricesMax?: {
    USD?: number;
    EUR?: number;
    UAH?: number;
    GEL?: number;
  };
  lastCurrencyUpdate?: string;
  originalPrice?: {
    amount: number;
    currency: string;
  };
  originalPricePerSqM?: {
    amount: number;
    currency: string;
  };
  _id?: string;
}

export interface StandardAdditionalInfo {
  heating?: string;
  walls?: string;
  insulation?: string;
  condition?: string;
  renovation?: string;
  parking?: string;
  floors?: number;
  floor?: number;
  has24_7Access?: boolean;
  isNewBuilding?: boolean;
  nearestMetroStation?: string[];
  _id?: string;
}

export interface StandardMediaGallery {
  propertyImages?: string[];
  _id?: string;
}

export interface StandardAmenities {
  sportsGround?: boolean;
  playground?: boolean;
  pool?: boolean;
  videoSurveillance?: boolean;
  security247?: boolean;
  terrace?: boolean;
  openPlan?: boolean;
  evCharging?: boolean;
  ownOSBB?: boolean;
  parking?: boolean;
  furnished?: boolean;
  _id?: string;
}

export interface StandardComfort {
  elevator?: boolean;
  garage?: boolean;
  parkingSpace?: boolean;
  concierge?: boolean;
  terrace?: boolean;
  floorHeating?: boolean;
  balconyLodge?: boolean;
  alarmSystem?: boolean;
  fireAlarm?: boolean;
  autonomousPower?: boolean;
  smartHomeTech?: boolean;
  bath?: boolean;
  showerCabin?: boolean;
  _id?: string;
}

export interface StandardProjectInfo {
  constructionStatus?: string;
  completionYear?: number;
  numberOfUnits?: number;
  numberOfBuildings?: number;
  numberOfStoreys?: number;
  files?: string[];
  _id?: string;
}

export interface StandardDeveloper {
  name?: string;
  _id?: string;
}

export interface StandardProject {
  name?: string;
  _id?: string;
}

export interface StandardRealtorFields {
  ownerPhone?: string[];
  customAdLinks?: string[];
  comments?: string;
  isExclusive?: boolean;
  isAgent?: boolean;
  callDate?: string;
  tags?: string[];
  ownerUserId?: string;
  responsibleAgent?: string;
  _id?: string;
}

export interface StandardUrl {
  language: string;
  url: string;
  _id?: string;
}

export interface StandardCreatorFields {
  createdDate?: string;
  createdBy?: string;
  responsible?: string;
  _id?: string;
}

/**
 * Main standardized property interface
 * Based on real MongoDB structure with all nested objects preserved
 */
export interface StandardProperty {
  _id: string;
  dealType: string;
  customId?: string;
  watchCount?: number;

  // Main nested structures (as they exist in DB)
  mainInfo: StandardMainInfo;
  location?: StandardLocation;
  city?: string;
  pricing?: StandardPricing;
  additionalInfo?: StandardAdditionalInfo;
  mediaGallery?: StandardMediaGallery;
  amenities?: StandardAmenities;
  comfort?: StandardComfort;
  projectInfo?: StandardProjectInfo;
  developer?: StandardDeveloper;
  project?: StandardProject;
  realtorFields?: StandardRealtorFields;
  creatorFields?: StandardCreatorFields;
  
  // Arrays
  urls?: StandardUrl[];
  layouts?: string[];
  translations?: string[];

  // System fields
  createdAt: string;
  updatedAt: string;
  __v?: number;
  slug?: string;
}

/**
 * Flattened property interface for UI components
 * This provides direct access to commonly used fields with fallbacks
 */
export interface FlattenedProperty {
  // Core identification
  id: string;
  customId?: string;
  dealType: string;
  slug?: string;

  // Basic info (flattened from mainInfo)
  title: string;
  description?: string;
  objectType?: string;
  class?: string;

  // Measurements
  area?: number;
  areaMin?: number;
  areaMax?: number;
  rooms?: number;
  bedrooms?: number;
  bathrooms?: number;
  ceilingHeight?: number;
  landArea?: number;

  // Location (flattened)
  address?: string;
  city?: string;
  location?: string; // For display purposes
  coordinates?: [number, number];
  district?: string;
  microdistrict?: string;
  country?: string;
  region?: string;

  // Building info (flattened from additionalInfo)
  floors?: number;
  floor?: number;
  heating?: string;
  walls?: string;
  insulation?: string;
  condition?: string;
  renovation?: string;
  parking?: string;
  has24_7Access?: boolean;
  isNewBuilding?: boolean;

  // Images
  images?: string[];

  // Pricing (preserved structure for flexibility)
  pricing?: StandardPricing;

  // System dates
  createdAt?: string;
  updatedAt?: string;

  // UI helpers
  redirectLink?: string;
  watchCount?: number;

  // Nested objects (for components that need full access)
  amenities?: StandardAmenities;
  comfort?: StandardComfort;
  projectInfo?: StandardProjectInfo;
  developer?: StandardDeveloper;
  project?: StandardProject;
  realtorFields?: StandardRealtorFields;

  // Translation data
  translations?: string[];
}

/**
 * Standard fallback values for missing data
 */
export const STANDARD_FALLBACKS = {
  title: "N/A",
  description: "",
  area: 0,
  rooms: 0,
  bedrooms: 0,
  bathrooms: 0,
  floor: 0,
  floors: 0,
  location: "N/A",
  city: "N/A",
  address: "N/A",
  images: [] as string[],
  customId: "N/A",
  dealType: "buy",
  objectType: "flat",
} as const;

/**
 * Utility function to flatten property data for UI consumption
 * Handles both nested (mainInfo/additionalInfo) and flat structures
 */
export function flattenPropertyData(
  property: StandardProperty,
): FlattenedProperty {
  const mainInfo = property.mainInfo || {};
  const additionalInfo = property.additionalInfo || {};
  const location = property.location || {};
  const mediaGallery = property.mediaGallery || {};

  return {
    // Core identification
    id: property._id || property.customId || STANDARD_FALLBACKS.customId,
    customId: property.customId,
    dealType: property.dealType || STANDARD_FALLBACKS.dealType,
    slug: property.slug,

    // Basic info from mainInfo
    title: mainInfo.title || STANDARD_FALLBACKS.title,
    description: mainInfo.description || STANDARD_FALLBACKS.description,
    objectType: mainInfo.objectType || STANDARD_FALLBACKS.objectType,
    class: mainInfo.class,

    // Measurements from mainInfo
    area: mainInfo.area || STANDARD_FALLBACKS.area,
    rooms: mainInfo.rooms || STANDARD_FALLBACKS.rooms,
    bedrooms: mainInfo.bedrooms || STANDARD_FALLBACKS.bedrooms,
    bathrooms: mainInfo.bathrooms || STANDARD_FALLBACKS.bathrooms,
    ceilingHeight: mainInfo.ceilingHeight,
    landArea: mainInfo.landArea,

    // Location flattened
    address: mainInfo.address || location.city || STANDARD_FALLBACKS.address,
    city: location.city || STANDARD_FALLBACKS.city,
    location: location.city || STANDARD_FALLBACKS.location,
    coordinates: location.location?.coordinates,
    district: location.district,
    microdistrict: location.microdistrict,
    country: location.country,
    region: location.region,

    // Building info from additionalInfo
    floors: additionalInfo.floors || STANDARD_FALLBACKS.floors,
    floor: additionalInfo.floor || STANDARD_FALLBACKS.floor,
    heating: additionalInfo.heating,
    walls: additionalInfo.walls,
    insulation: additionalInfo.insulation,
    condition: additionalInfo.condition,
    renovation: additionalInfo.renovation,
    parking: additionalInfo.parking,
    has24_7Access: additionalInfo.has24_7Access,
    isNewBuilding: additionalInfo.isNewBuilding,

    // Images from mediaGallery
    images: mediaGallery.propertyImages || STANDARD_FALLBACKS.images,

    // Pricing preserved as-is
    pricing: property.pricing,

    // System dates
    createdAt: property.createdAt,
    updatedAt: property.updatedAt,

    // UI helpers
    watchCount: property.watchCount,

    // Nested objects preserved for components that need them
    amenities: property.amenities,
    comfort: property.comfort,
    projectInfo: property.projectInfo,
    developer: property.developer,
    project: property.project,
    realtorFields: property.realtorFields,

    // Translation data
    translations: property.translations,
  };
}

/**
 * Enhanced property interface for authenticated users (realtors)
 * Includes all fields from StandardProperty plus guaranteed creatorFields, urls
 */
export interface AuthenticatedProperty extends StandardProperty {
  creatorFields: StandardCreatorFields; // Required for authenticated access
  urls: StandardUrl[]; // Required - 6 language URLs
  realtorFields?: StandardRealtorFields & {
    ownerUserId?: string; // May be available for authenticated users
  };
}

/**
 * Public property interface - excludes sensitive realtor data
 */
export interface PublicProperty extends Omit<StandardProperty, 'creatorFields' | 'urls'> {
  creatorFields?: never; // Explicitly exclude
  urls?: never; // Explicitly exclude
  realtorFields?: Omit<StandardRealtorFields, 'ownerUserId' | 'ownerPhone' | 'comments'>; // Limited realtor data
}

/**
 * Type guard to check if property has nested structure
 */
export function hasNestedStructure(
  property: any,
): property is StandardProperty {
  return (
    property &&
    typeof property.mainInfo === "object" &&
    property.mainInfo !== null
  );
}

/**
 * Type guard to check if property has enhanced fields (authenticated response)
 */
export function isAuthenticatedProperty(property: any): property is AuthenticatedProperty {
  return property && 
         property.creatorFields && 
         Array.isArray(property.urls) && 
         property.urls.length > 0;
}

/**
 * Utility to safely access nested property fields with fallbacks
 */
export function safePropertyAccess<T>(
  property: any,
  path: string,
  fallback: T,
): T {
  try {
    const keys = path.split(".");
    let current = property;

    for (const key of keys) {
      if (current && typeof current === "object" && key in current) {
        current = current[key];
      } else {
        return fallback;
      }
    }

    return current !== undefined && current !== null ? current : fallback;
  } catch {
    return fallback;
  }
}
